import { useState } from 'react';

import { AiActionButton } from './AiActionButton';

import { ConfirmDialog } from '@/components/ui/confirm-dialog';

export interface RegenerateSizeChipProps {
  aspectRatio: string;
  disabled?: boolean;
  disabledReason?: string;
  onConfirm: () => void;
  className?: string;
}

/**
 * "Regenerate" for a size that already has an image: makes a new image from the Concept with the
 * current renderer, so a card made before the renderer improved can be refreshed. It is an action,
 * not a toggle, so it lives in the Size variants header (never in the prompt-pill scroller, where it
 * scrolled out of sight) in the shared AI-action treatment. Confirmed first because
 * the size switches to the new image; the old one stays in Assets.
 */
export function RegenerateSizeChip({
  aspectRatio,
  disabled,
  disabledReason,
  onConfirm,
  className,
}: RegenerateSizeChipProps): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <AiActionButton
        disabled={disabled}
        title={disabled ? disabledReason : 'Make a new image for this size from the Concept'}
        onClick={() => setOpen(true)}
        className={className}
      >
        Regenerate
      </AiActionButton>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`Regenerate the ${aspectRatio} image?`}
        description="This makes a new image from the Concept and replaces this size with it. Edits made to this size's image are not carried over. The current image stays in your Assets, and generating costs one image credit."
        confirmLabel="Regenerate"
        onConfirm={() => {
          setOpen(false);
          onConfirm();
        }}
      />
    </>
  );
}

export interface RegenerateAllSizesChipProps {
  /** Aspect ratios that will be regenerated, in the order they run. */
  aspectRatios: readonly string[];
  /** The size being made right now (1-based position in `aspectRatios`), or null when idle. */
  runningIndex: number | null;
  disabled?: boolean;
  disabledReason?: string;
  onConfirm: () => void;
  className?: string;
}

/**
 * "Regenerate all sizes": the same action as {@link RegenerateSizeChip}, run for every size one
 * after another, so a typographic Concept whose sizes were made by different renderer versions can
 * be brought to one look. Confirmed first because every size is replaced and each costs one image
 * credit; the old images stay in Assets.
 */
export function RegenerateAllSizesChip({
  aspectRatios,
  runningIndex,
  disabled,
  disabledReason,
  onConfirm,
  className,
}: RegenerateAllSizesChipProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const running = runningIndex !== null;
  const count = aspectRatios.length;
  return (
    <>
      <AiActionButton
        disabled={disabled}
        busy={running}
        title={
          disabled
            ? disabledReason
            : 'Make a new image for every size from the Concept, one after another'
        }
        onClick={() => setOpen(true)}
        className={className}
      >
        {running
          ? `Regenerating ${aspectRatios[runningIndex] ?? ''} (${runningIndex + 1} of ${count})`
          : 'Regenerate all sizes'}
      </AiActionButton>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`Regenerate all ${count} sizes?`}
        description={`This makes a new image from the Concept for ${aspectRatios.join(', ')} and replaces each size with it, one at a time. Edits made to any size's image are not carried over. The current images stay in your Assets, and generating costs ${count} image credits.`}
        confirmLabel="Regenerate all"
        onConfirm={() => {
          setOpen(false);
          onConfirm();
        }}
      />
    </>
  );
}
